import {
  resolvePickedFollowUpPrompt,
  selectPendingFollowUp,
} from './shared-responder-v2-core-follow-up-prompt.helpers';
import {
  isReplyingToFollowUp,
  isThreadAnswerLocked,
} from './shared-responder-v2-core-follow-up-reply.helpers';
import {
  resolveFollowUpSpokenAffordances,
  resolveSpokenAffordances,
} from './shared-responder-v2-core-handsfree.affordances';
import {
  resolveHandsFreeReplayCursor,
  resolveHandsFreeScript,
  resolveHandsFreeUpcomingScripts,
} from './shared-responder-v2-core-handsfree.script';
import {
  endsWithSpokenCommand,
  resolveSpokenDraftEvent,
} from './shared-responder-v2-core-handsfree.speech';
import {
  DELIVERY_CHANNELS,
  HANDS_FREE_STATUSES,
  type HandsFreeScript,
  type HandsFreeState,
  type SpokenAffordances,
} from './shared-responder-v2-core-handsfree.types';
import { RESPONDER_PHASES } from './shared-responder-v2-core-session.consts';
import {
  isThreadInFlight,
  selectCurrentSpec,
  selectCurrentThread,
  selectIsBlockedBySiteGate,
} from './shared-responder-v2-core-session.selectors';
import type { ResponderSessionState } from './shared-responder-v2-core-session.types';
import {
  SPOKEN_PICK_STATUSES,
  type SpokenPickStatus,
} from './shared-responder-v2-core-spoken-pick.consts';
import type { ResponderDraftEvent } from './shared-responder-v2-core-thread-behaviour';
import { resolveSelectedOtherText } from './shared-responder-v2-core-thread-choice';
import { getThreadBehaviour } from './shared-responder-v2-core-thread-registry';

/**
 * What the loop should do next. `draft` and `advance` are *ordinary* session
 * intents — hands-free drives the existing machine and owns no forward
 * transition of its own.
 */
export type HandsFreeAction =
  | { readonly type: 'idle' }
  | { readonly type: 'speak'; readonly script: HandsFreeScript }
  | { readonly type: 'listen' }
  | { readonly type: 'draft'; readonly event: ResponderDraftEvent }
  | { readonly type: 'draftThenAdvance'; readonly event: ResponderDraftEvent }
  | { readonly type: 'advance' }
  | { readonly type: 'open' };

const IDLE_ACTION: HandsFreeAction = { type: 'idle' };

const LISTEN_ACTION: HandsFreeAction = { type: 'listen' };

const PREFETCH_DEPTH = 2;

export function isHandsFreeDelivery(state: ResponderSessionState): boolean {
  return state.capabilities.deliveryChannel === DELIVERY_CHANNELS.HandsFree;
}

export function selectShouldArmHandsFree(
  state: ResponderSessionState
): boolean {
  return (
    isHandsFreeDelivery(state) && state.phase === RESPONDER_PHASES.Question
  );
}

export function selectHandsFreeAffordances(
  state: ResponderSessionState
): SpokenAffordances | null {
  const spec = selectCurrentSpec(state);

  return spec === null
    ? null
    : resolveSpokenAffordances(getThreadBehaviour(spec.kind));
}

export function selectHandsFreeResumeCursor(
  state: ResponderSessionState,
  handsFree: HandsFreeState
): string | null {
  const thread = selectCurrentThread(state);

  return isThreadInFlight(thread)
    ? handsFree.spokenScriptKey
    : resolveHandsFreeReplayCursor(state);
}

export function selectHandsFreePrefetchScripts(
  state: ResponderSessionState,
  handsFree: HandsFreeState
): readonly HandsFreeScript[] {
  const thread = selectCurrentThread(state);

  if (
    !selectShouldArmHandsFree(state) ||
    thread === null ||
    isThreadInFlight(thread)
  ) {
    return [];
  }

  return resolveHandsFreeUpcomingScripts(
    state,
    handsFree.spokenScriptKey,
    PREFETCH_DEPTH
  );
}

export function selectAwaitsSpokenOpen(state: ResponderSessionState): boolean {
  return selectIsBlockedBySiteGate(state) && state.mobileAppEmbedUrl === null;
}

export function selectActiveSpokenAffordances(
  state: ResponderSessionState
): SpokenAffordances | null {
  const spec = selectCurrentSpec(state);
  const thread = selectCurrentThread(state);

  if (spec === null || thread === null) {
    return null;
  }

  return isReplyingToFollowUp(state, spec.questionId)
    ? resolveFollowUpSpokenAffordances(
        resolvePickedFollowUpPrompt(thread.kind, selectPendingFollowUp(thread))
      )
    : resolveSpokenAffordances(getThreadBehaviour(spec.kind));
}

function rewritesOtherText(
  event: ResponderDraftEvent,
  otherText: string
): boolean {
  return event.type === 'otherTextChanged' && event.text !== otherText;
}

export function resolveHandsFreeAction(input: {
  readonly handsFree: HandsFreeState;
  readonly script: HandsFreeScript | null;
  readonly affordances: SpokenAffordances;
  readonly draftEvent: ResponderDraftEvent | null;
  readonly isContinueCommand: boolean;
  readonly isOpenCommand: boolean;
  /** `true` while the session is waiting on the gateway for this question. */
  readonly isTurnInFlight: boolean;
  readonly awaitedOtherText: string | null;
  readonly spokenPickStatus: SpokenPickStatus;
}): HandsFreeAction {
  const {
    affordances,
    awaitedOtherText,
    draftEvent,
    handsFree,
    isContinueCommand,
    script,
  } = input;

  // The loop stands down entirely while a turn is in flight: this is what stops
  // a second utterance from issuing a duplicate `addQuestionMessage`, and it
  // reads the session's own in-flight state rather than mirroring it.
  if (input.isTurnInFlight) {
    return IDLE_ACTION;
  }

  switch (handsFree.status) {
    case HANDS_FREE_STATUSES.Dormant:
    case HANDS_FREE_STATUSES.Ended:
    case HANDS_FREE_STATUSES.Paused:
    case HANDS_FREE_STATUSES.Speaking:
      return IDLE_ACTION;

    case HANDS_FREE_STATUSES.Pending:
    case HANDS_FREE_STATUSES.Listening:
      return script === null ? LISTEN_ACTION : { type: 'speak', script };

    case HANDS_FREE_STATUSES.Capturing:
      if (input.isOpenCommand) {
        return { type: 'open' };
      }

      return affordances.draftsSpokenAnswer && draftEvent !== null
        ? { type: 'draft', event: draftEvent }
        : LISTEN_ACTION;

    case HANDS_FREE_STATUSES.Captured:
      if (awaitedOtherText !== null) {
        if (
          draftEvent !== null &&
          rewritesOtherText(draftEvent, awaitedOtherText)
        ) {
          return isContinueCommand
            ? { type: 'draftThenAdvance', event: draftEvent }
            : { type: 'draft', event: draftEvent };
        }

        if (isContinueCommand) {
          return { type: 'advance' };
        }

        return {
          type: 'draft',
          event: { type: 'otherTextChanged', text: awaitedOtherText },
        };
      }

      if (!isContinueCommand) {
        return LISTEN_ACTION;
      }

      return affordances.extractsSpokenPick &&
        input.spokenPickStatus === SPOKEN_PICK_STATUSES.Pending
        ? IDLE_ACTION
        : { type: 'advance' };
    default: {
      const unexpected: never = handsFree.status;
      throw new Error('Unexpected hands-free status: ' + String(unexpected));
    }
  }
}

/**
 * The session-aware wrapper the loop hook calls: everything the decision needs
 * is read from the session state here, so the decision itself stays a pure
 * function of plain values and is spec'd without a renderer.
 */
export function selectHandsFreeAction(
  state: ResponderSessionState,
  handsFree: HandsFreeState,
  commands: readonly string[],
  spokenPickStatus: SpokenPickStatus,
  openCommands: readonly string[]
): HandsFreeAction {
  const spec = selectCurrentSpec(state);
  const thread = selectCurrentThread(state);
  const affordances = selectActiveSpokenAffordances(state);

  if (spec === null || thread === null || affordances === null) {
    return IDLE_ACTION;
  }

  const isReplying = isReplyingToFollowUp(state, spec.questionId);
  const transcript = handsFree.transcript ?? '';
  const { draft } = thread;
  const awaitedOtherText =
    isReplying || isThreadAnswerLocked(state, spec.questionId)
      ? null
      : resolveSelectedOtherText(draft);

  return resolveHandsFreeAction({
    affordances,
    draftEvent: resolveSpokenDraftEvent({
      affordances,
      commands,
      isAwaitingOtherText: awaitedOtherText !== null,
      transcript,
    }),
    awaitedOtherText,
    handsFree,
    isContinueCommand: endsWithSpokenCommand(transcript, commands),
    isOpenCommand:
      selectAwaitsSpokenOpen(state) &&
      endsWithSpokenCommand(transcript, openCommands),
    isTurnInFlight: isThreadInFlight(thread),
    script: resolveHandsFreeScript(state, handsFree.spokenScriptKey),
    spokenPickStatus,
  });
}
